import { c as _c } from "react/compiler-runtime";
import { RefreshCw, ServerCrash } from '@/components/ui/icons/Icons';
import Image from 'next/image';
import { Component, ErrorInfo, ReactNode } from 'react';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  errorTitle?: string;
  errorDescription?: string;
  refreshButton?: string;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

// Patterns matched against error STACK TRACES to identify extension-sourced errors
const EXTENSION_SOURCE_PATTERNS = [/chrome-extension:\/\//i, /moz-extension:\/\//i, /safari-extension:\/\//i];

// Patterns matched against error MESSAGE only.
// These errors are caused by third-party DOM modifications (Google Translate,
// antivirus extensions like Bitdefender/Norton/Kaspersky/McAfee, etc.).
// React's own code never produces these because it tracks its own DOM tree.
// IMPORTANT: Never match these against stack traces — React DOM method names
// appear in every stack trace and would cause false positives.
const THIRD_PARTY_DOM_ERROR_PATTERNS = [/Failed to execute '(removeChild|insertBefore|appendChild)' on 'Node'/i, /The node to be removed is not a child/i, /NotFoundError:.*removeChild/i, /Cannot read properties of null.*reading '(removeChild|insertBefore|appendChild|style)'/i, /Node\.removeChild.*is not a child/i];
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }
  private isThirdPartyError(error: Error, errorInfo?: ErrorInfo): boolean {
    const message = error.message || '';
    const stackSources = `${error.stack || ''} ${errorInfo?.componentStack || ''}`;

    // Check stack traces for extension:// URLs
    if (EXTENSION_SOURCE_PATTERNS.some(p => p.test(stackSources))) return true;

    // Check error message for DOM manipulation errors caused by third-party modifications
    if (THIRD_PARTY_DOM_ERROR_PATTERNS.some(p => p.test(message))) return true;
    return false;
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.isThirdPartyError(error, errorInfo)) {
      // eslint-disable-next-line no-console
      console.debug('[ErrorBoundary] Suppressed third-party DOM error:', error.message);
      setTimeout(() => {
        this.setState({
          hasError: false,
          error: null
        });
      }, 100);
    } else {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Application error:', error, errorInfo);
    }
  }
  render() {
    if (this.state.hasError) {
      // Third-party error (extension or Google Translate / antivirus DOM modification)
      // → try to render children normally since the error was transient
      if (this.state.error && this.isThirdPartyError(this.state.error)) {
        return this.props.children;
      }

      // For real app errors, show fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <div className="flex min-h-screen flex-col bg-background">
          {/* Subtle background gradient */}
          <div className="pointer-events-none fixed inset-0 bg-gradient-to-b from-primary/[0.03] via-transparent to-primary/[0.02]" />

          {/* Top-left brand */}
          <div className="relative z-10 p-6 sm:p-8">
            <div className="flex items-center gap-2.5">
              <Image src="/images/orsay-mark.svg" alt="Orsay" width={40} height={40} className="h-10 w-10" priority />
              <span className="font-serif text-[22px] font-medium tracking-tight text-op-ink">
                Orsay
              </span>
            </div>
          </div>

          {/* Centered error content */}
          <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 pb-16">
            <div className="flex max-w-md flex-col items-center text-center">
              {/* Error icon — no background */}
              <ServerCrash className="mb-6 size-16 text-muted-foreground/50" strokeWidth={1.5} />

              {/* Heading */}
              <h1 className="mb-2 text-lg font-semibold text-foreground">
                {this.props.errorTitle || 'Une erreur est survenue'}
              </h1>
              <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                {this.props.errorDescription || 'Veuillez essayer de rafraîchir la page. Si le problème persiste, essayez de désactiver les extensions du navigateur.'}
              </p>

              {/* Refresh button */}
              <button onClick={() => {
              this.setState({
                hasError: false,
                error: null
              });
              window.location.reload();
            }} className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <RefreshCw className="size-4" />
                {this.props.refreshButton || 'Rafraîchir la page'}
              </button>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}

// Wrapper component to provide translations to the class component
import { useI18n } from '@/context/I18nProvider';
const TranslatedErrorBoundary = t0 => {
  const $ = _c(12);
  const {
    children,
    fallback
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== t) {
    t1 = t("errorBoundary.somethingWentWrong");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("errorBoundary.refreshDescription");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("errorBoundary.refreshPage");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== children || $[7] !== fallback || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <ErrorBoundary fallback={fallback} errorTitle={t1} errorDescription={t2} refreshButton={t3}>{children}</ErrorBoundary>;
    $[6] = children;
    $[7] = fallback;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
export { ErrorBoundary, TranslatedErrorBoundary };